/**
 * Auto-generated service - DO NOT EDIT MANUALLY
 * Generated from: mailbox.ts
 * Run: pnpm gen
 */

import { API_METHODS } from '../constants/api-methods';
import { getApiPath } from '../path';
import { type RequestOptions, type Transport } from '../transport';

import type {
  BulkDeleteMailboxMessagesRequest,
  BulkDeleteMailboxMessagesResponse,
  DeleteAdminMailboxMessageRequest,
  DeleteAdminMailboxMessageResponse,
  DeleteMailboxMessageRequest,
  DeleteMailboxMessageResponse,
  GetAdminMailboxInboxRequest,
  GetAdminMailboxInboxResponse,
  GetAdminMailboxMessageRequest,
  GetAdminMailboxMessageResponse,
  GetMailboxInboxRequest,
  GetMailboxInboxResponse,
  GetMailboxMessageRequest,
  GetMailboxMessageResponse,
  GetMailboxUnreadCountRequest,
  GetMailboxUnreadCountResponse,
  PurgeAdminMailboxMessagesRequest,
  PurgeAdminMailboxMessagesResponse,
  SendMailboxMessageRequest,
  SendMailboxMessageResponse,
  SendUserMailboxMessageRequest,
  SendUserMailboxMessageResponse,
  StreamMailboxNotificationsRequest,
  StreamMailboxNotificationsResponse,
  UpdateAdminMailboxMessageFlagsRequest,
  UpdateAdminMailboxMessageFlagsResponse,
  UpdateMailboxMessageFlagsRequest,
  UpdateMailboxMessageFlagsResponse,
} from '../types/mailbox';

export const createMailboxService = (transport: Transport) => ({
  async getMailboxInbox(
    request: GetMailboxInboxRequest,
    options: RequestOptions = {},
  ): Promise<GetMailboxInboxResponse> {
    const url = getApiPath(API_METHODS.MailboxService.GetMailboxInbox);
    return transport.get<GetMailboxInboxResponse>(url, { params: request, ...options });
  },

  async getMailboxMessage(
    request: GetMailboxMessageRequest,
    options: RequestOptions = {},
  ): Promise<GetMailboxMessageResponse> {
    const { message_id } = request;
    const url = getApiPath(API_METHODS.MailboxService.GetMailboxMessage, { message_id });
    return transport.get<GetMailboxMessageResponse>(url, options);
  },

  async updateMailboxMessageFlags(
    request: UpdateMailboxMessageFlagsRequest,
    options: RequestOptions = {},
  ): Promise<UpdateMailboxMessageFlagsResponse> {
    const { message_id, ...body } = request;
    const url = getApiPath(API_METHODS.MailboxService.UpdateMailboxMessageFlags, { message_id });
    return transport.post<UpdateMailboxMessageFlagsResponse>(url, body, options);
  },

  async sendMailboxMessage(
    request: SendMailboxMessageRequest,
    options: RequestOptions = {},
  ): Promise<SendMailboxMessageResponse> {
    const url = getApiPath(API_METHODS.MailboxService.SendMailboxMessage);
    return transport.post<SendMailboxMessageResponse>(url, request, options);
  },

  async getAdminMailboxInbox(
    request: GetAdminMailboxInboxRequest,
    options: RequestOptions = {},
  ): Promise<GetAdminMailboxInboxResponse> {
    const { mailbox_id, ...params } = request;
    const url = getApiPath(API_METHODS.MailboxService.GetAdminMailboxInbox, { mailbox_id });
    return transport.get<GetAdminMailboxInboxResponse>(url, { params, ...options });
  },

  async getMailboxUnreadCount(
    _request: GetMailboxUnreadCountRequest,
    options: RequestOptions = {},
  ): Promise<GetMailboxUnreadCountResponse> {
    const url = getApiPath(API_METHODS.MailboxService.GetMailboxUnreadCount);
    return transport.get<GetMailboxUnreadCountResponse>(url, options);
  },

  async deleteMailboxMessage(
    request: DeleteMailboxMessageRequest,
    options: RequestOptions = {},
  ): Promise<DeleteMailboxMessageResponse> {
    const { message_id } = request;
    const url = getApiPath(API_METHODS.MailboxService.DeleteMailboxMessage, { message_id });
    return transport.delete<DeleteMailboxMessageResponse>(url, options);
  },

  async getAdminMailboxMessage(
    request: GetAdminMailboxMessageRequest,
    options: RequestOptions = {},
  ): Promise<GetAdminMailboxMessageResponse> {
    const { mailbox_id, message_id } = request;
    const url = getApiPath(API_METHODS.MailboxService.GetAdminMailboxMessage, {
      mailbox_id,
      message_id,
    });
    return transport.get<GetAdminMailboxMessageResponse>(url, options);
  },

  async updateAdminMailboxMessageFlags(
    request: UpdateAdminMailboxMessageFlagsRequest,
    options: RequestOptions = {},
  ): Promise<UpdateAdminMailboxMessageFlagsResponse> {
    const { mailbox_id, message_id, ...body } = request;
    const url = getApiPath(API_METHODS.MailboxService.UpdateAdminMailboxMessageFlags, {
      mailbox_id,
      message_id,
    });
    return transport.post<UpdateAdminMailboxMessageFlagsResponse>(url, body, options);
  },

  async deleteAdminMailboxMessage(
    request: DeleteAdminMailboxMessageRequest,
    options: RequestOptions = {},
  ): Promise<DeleteAdminMailboxMessageResponse> {
    const { mailbox_id, message_id } = request;
    const url = getApiPath(API_METHODS.MailboxService.DeleteAdminMailboxMessage, {
      mailbox_id,
      message_id,
    });
    return transport.delete<DeleteAdminMailboxMessageResponse>(url, options);
  },

  async purgeAdminMailboxMessages(
    request: PurgeAdminMailboxMessagesRequest,
    options: RequestOptions = {},
  ): Promise<PurgeAdminMailboxMessagesResponse> {
    const { mailbox_id, ...body } = request;
    const url = getApiPath(API_METHODS.MailboxService.PurgeAdminMailboxMessages, { mailbox_id });
    return transport.post<PurgeAdminMailboxMessagesResponse>(url, body, options);
  },

  async sendUserMailboxMessage(
    request: SendUserMailboxMessageRequest,
    options: RequestOptions = {},
  ): Promise<SendUserMailboxMessageResponse> {
    const url = getApiPath(API_METHODS.MailboxService.SendUserMailboxMessage);
    return transport.post<SendUserMailboxMessageResponse>(url, request, options);
  },

  async bulkDeleteMailboxMessages(
    request: BulkDeleteMailboxMessagesRequest,
    options: RequestOptions = {},
  ): Promise<BulkDeleteMailboxMessagesResponse> {
    const url = getApiPath(API_METHODS.MailboxService.BulkDeleteMailboxMessages);
    return transport.post<BulkDeleteMailboxMessagesResponse>(url, request, options);
  },

  async streamMailboxNotifications(
    request: StreamMailboxNotificationsRequest,
    options: RequestOptions = {},
  ): Promise<StreamMailboxNotificationsResponse> {
    const url = getApiPath(API_METHODS.MailboxService.StreamMailboxNotifications);
    return transport.get<StreamMailboxNotificationsResponse>(url, { params: request, ...options });
  },
});

export type MailboxService = ReturnType<typeof createMailboxService>;
